import MaterialIcon from '@components/common/MaterialIcon';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import CheckboxTogglerT from '@components/tailwind-components/Input/CheckboxTogglerT';
import { STUDIO_TYPE_LABELS } from '@conversed/shared/src/central/types/studio.type';
import { useDataSourceActions } from '@hooks/dataSources/useDataSourceActions';
import { useDataSources } from '@hooks/dataSources/useDataSources';
import { useModal } from '@hooks/modal/useModal';
import { useCompanySelector } from '@hooks/useCompanySelector';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const ShareDataSource: React.FC = () => {
  const { selectedDataSourceFile } = useDataSources();
  const { updateDataSourceDetails } = useDataSourceActions();
  const { siblingStudios } = useCompanySelector();
  const { hideModal } = useModal();
  const { t } = useTranslation();
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(
    selectedDataSourceFile?.sharedWithCompanyIds || [],
  );

  const handleToggleStudio = (studioId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(studioId)
        ? prev.filter((id) => id !== studioId)
        : [...prev, studioId],
    );
  };

  const handleShare = async () => {
    if (!selectedDataSourceFile?.key) {
      return;
    }

    await updateDataSourceDetails({
      key: selectedDataSourceFile.key,
      sharedWithCompanyIds: selectedCompanyIds,
    });
    hideModal();
  };

  if (!selectedDataSourceFile) {
    return null;
  }

  return (
    <div className='tailwind-modal-form'>
      <div className='tailwind-modal-body'>
        <div className='grid grid-cols-1 gap-x-6 gap-y-2'>
          {/* Description */}
          <div className='col-span-1'>
            <p className='text-sm'>
              {t('data-sources.share.description', {
                fileName: selectedDataSourceFile.name,
              })}
            </p>
          </div>

          <div className='tailwind-divider-x' />

          {/* Studio selection */}
          {siblingStudios.length === 0 ? (
            <div className='col-span-1'>
              <div className='text-center py-8 text-gray-500 bg-gray-100 rounded-md border border-gray-300'>
                <MaterialIcon
                  icon='info'
                  classes={['text-4xl', 'mb-2']}
                  colorHex='#9ca3af'
                />
                <p className='text-sm'>
                  {t('data-sources.share.no-studios-available')}
                </p>
              </div>
            </div>
          ) : (
            <div className='col-span-1'>
              <h4 className='text-sm font-medium text-gray-600 flex items-center'>
                {t('data-sources.share.select-studios')}
              </h4>
              <p className='flex items-center text-tiny text-gray-500 italic mb-3'>
                {t('data-sources.share.restriction-hint')}
              </p>
              <div className='space-y-2 max-h-[300px] overflow-y-auto pr-2'>
                {siblingStudios.map((studio, index) => {
                  const isChecked = selectedCompanyIds.includes(studio.id);
                  const studioLabel = `${studio.name} - ${STUDIO_TYPE_LABELS[studio.studioType]}`;

                  return (
                    <div
                      key={studio.id}
                      className={`
                        flex items-center gap-3 p-3 border rounded-md transition-all cursor-pointer
                        ${
                          isChecked
                            ? 'border-primary-base bg-primary-50'
                            : 'border-gray-300 bg-white hover:bg-gray-100'
                        }
                      `}
                      onClick={() => handleToggleStudio(studio.id)}>
                      <CheckboxTogglerT
                        id={`share-toggle-${index}`}
                        toggleSize='base'
                        colorScheme='orangeBlue'
                        checked={isChecked}
                        onChange={() => {
                          handleToggleStudio(studio.id);
                        }}
                      />
                      <div className='flex-1'>
                        <div className='text-sm font-medium text-gray-800'>
                          {studioLabel}
                        </div>
                      </div>
                      {isChecked && (
                        <MaterialIcon
                          icon='check_circle'
                          classes={[
                            'text-lg',
                            'text-green-dark',
                            'leading-none',
                          ]}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='tailwind-modal-footer'>
        <ButtonT
          type='button'
          styling='cancel'
          size='xs'
          rounding='md'
          onClick={hideModal}>
          {t('general.buttons.cancel')}
        </ButtonT>
        <ButtonT
          type='button'
          styling='primary'
          size='xs'
          rounding='md'
          onClick={handleShare}
          disabled={siblingStudios.length === 0}>
          {t('general.buttons.share')}
        </ButtonT>
      </div>
    </div>
  );
};

export default ShareDataSource;
